import React from 'react';
import useStyles from './styles';
import Stack from '@mui/material/Stack';
import { formatPrice, PaymentType } from '@elevator/ui';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import clsx from 'clsx';
import SectionHeader from '../SectionHeader/SectionHeader';

/**
 * Line item for OrderSummary display.
 */
export interface OrderSummaryItem {
  /** Optional unique id (e.g. serviceId) */
  id?: string;
  /** Display name (e.g. service name) */
  name: string;
  /** Price in cents */
  cost: number;
  /** If true, rendered indented as an add-on */
  isAddOn?: boolean;
}

/**
 * OrderSummary Component
 *
 * Displays an order summary with an optional title, line items (name + price),
 * and a total. Add-ons are shown indented. Optional paymentType 'Free' shows
 * "Preferred Submission" and $0 for display purposes.
 *
 * Used in the checkout step for order summary display.
 * Does not depend on CartContext; pass items and total as props.
 *
 * @param props - Component props
 * @param props.title - Heading above the summary (default: "Order Summary")
 * @param props.items - Line items to display
 * @param props.total - Total amount in cents
 * @param props.paymentType - When 'Free', first-line display shows "Preferred Submission" / $0
 * @param props.emptyMessage - Message when items array is empty
 */
const OrderSummary = ({
  title = 'Order Summary',
  items,
  total,
  paymentType,
  emptyMessage = 'No items in cart.',
}: OrderSummaryProps) => {
  const { classes } = useStyles();

  const hasItems = Array.isArray(items) && items.length > 0;
  const hasTotal = typeof total === 'number';

  // No items and no usable total: render empty state.
  if (!hasItems && !hasTotal) {
    return (
      <Stack
        component="section"
        className={classes.root}
        direction="column"
        spacing={2}
        data-component="OrderSummary"
      >
        {title && <SectionHeader title={title} titleVariant="h6" divider={false} />}
        <div className={classes.summary}>
          <Typography variant="body1" color="textSecondary">
            {emptyMessage}
          </Typography>
        </div>
      </Stack>
    );
  }

  /** Matches per-row prices: preferred base is $0; paid add-ons still count. */
  const totalCentsForDisplay =
    paymentType === 'Free' && hasItems
      ? items!.reduce((sum, item) => sum + (item.isAddOn ? Number(item.cost) : 0), 0)
      : total!;

  const renderRow = (item: OrderSummaryItem, index: number) => {
    const nameString =
      paymentType === 'Free' && !item.isAddOn ? 'Preferred Submission' : item.name;
    const priceInCents = paymentType === 'Free' && !item.isAddOn ? 0 : item.cost;
    const formattedPrice = formatPrice(priceInCents);
    const isAddOn = Boolean(item.isAddOn);

    return (
      <Stack
        key={item.id ?? `item-${index}`}
        direction="row"
        alignItems="flex-end"
        className={isAddOn ? clsx(classes.service, classes.addOn) : classes.service}
      >
        <Typography variant={isAddOn ? 'body2' : 'body1'}>{nameString}</Typography>
        <span className={classes.serviceDots} aria-hidden />
        <Typography variant={isAddOn ? 'body2' : 'body1'}>${formattedPrice}</Typography>
      </Stack>
    );
  };

  return (
    <Stack
      component="section"
      className={classes.root}
      direction="column"
      spacing={2}
      data-component="OrderSummary"
    >
      {title && <SectionHeader title={title} titleVariant="h6" divider={false} />}
      <div className={classes.summary}>
        {hasItems && (
          <>
            <Stack direction="column">
              {items!.map((item, index) => renderRow(item, index))}
            </Stack>

            <Divider variant="fullWidth" />
          </>
        )}

        {hasTotal && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className={classes.totalLine}
          >
            <Typography variant="subtitle1">Total</Typography>
            <Typography variant="subtitle1">
              ${formatPrice(totalCentsForDisplay)}
            </Typography>
          </Stack>
        )}
      </div>
    </Stack>
  );
};

export interface OrderSummaryProps {
  /** Heading above the summary (default: "Order Summary") */
  title?: string;
  /** Line items (name, cost in cents, optional isAddOn). Optional if only a total is available. */
  items?: OrderSummaryItem[];
  /** Total amount in cents. Optional when only line items are displayed. */
  total?: number;
  /** When 'Free', shows "Preferred Submission" / $0 for non-add-on items */
  paymentType?: PaymentType;
  /** Message when items is empty */
  emptyMessage?: string;
}

export default OrderSummary;
